import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/hooks/use-api-query';
import { qk } from '@/lib/query/keys';

/** Row of GET /organizations/:orgId/team (all org members since 2026-06-12). */
export interface TeamMemberRow {
  publicId: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  role: string;
  source: 'team' | 'workspace_scoped';
  workspaces: { publicId: string; name: string; memberPublicId: string; role: string }[];
  createdAt: string;
}

export interface OrgTeamWorkspaceOption {
  publicId: string;
  name: string;
}

interface OrgTeamResponse {
  members: TeamMemberRow[];
}

interface OrgWorkspacesResponse {
  workspaces: OrgTeamWorkspaceOption[];
}

interface InviteTeamMemberVars {
  email: string;
  role: 'member' | 'admin';
  workspacePublicIds?: string[];
}

interface UpdateTeamMemberProfileVars {
  memberId: string;
  firstName: string;
  lastName: string;
  phone: string;
}

interface UpdateTeamMemberRoleVars {
  memberId: string;
  role: 'admin' | 'member';
}

interface AssignTeamMemberWorkspaceVars {
  workspacePublicId: string;
  organizationMemberPublicId: string;
  role: 'admin' | 'member';
}

interface UnassignTeamMemberWorkspaceVars {
  workspacePublicId: string;
  memberPublicId: string;
}

export function useOrgTeam(orgId: string | undefined, enabled = true) {
  return useApiQuery<OrgTeamResponse>(
    qk.org(orgId ?? '', 'team'),
    `/organizations/${orgId}/team`,
    { enabled: !!orgId && enabled },
  );
}

export function useOrgTeamWorkspaceOptions(
  orgId: string | undefined,
  enabled: boolean,
) {
  return useApiQuery<OrgWorkspacesResponse>(
    // Distinct key from the channels picker: same endpoint, different ?kind slice.
    qk.org(orgId ?? '', 'workspaces', 'team'),
    // Team-member assignment targets are team workspaces only.
    `/organizations/${orgId}/workspaces?kind=team`,
    { enabled: !!orgId && enabled },
  );
}

function useOrgTeamInvalidate(orgId: string | undefined) {
  const qc = useQueryClient();
  return () =>
    orgId ? qc.invalidateQueries({ queryKey: qk.org(orgId, 'team') }) : undefined;
}

function useOrgTeamAndWorkspaceMembersInvalidate(orgId: string | undefined) {
  const qc = useQueryClient();
  return (workspacePublicId: string) =>
    Promise.all([
      ...(orgId ? [qc.invalidateQueries({ queryKey: qk.org(orgId, 'team') })] : []),
      qc.invalidateQueries({ queryKey: qk.workspace(workspacePublicId, 'members') }),
    ]);
}

export function useInviteTeamMember(orgId: string | undefined) {
  return useApiMutation<void, InviteTeamMemberVars>(
    (vars) => ({
      path: `/organizations/${orgId}/team/invites`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(vars),
      },
      fallback: 'Failed to send the invite',
    }),
  );
}

export function useRemoveTeamMember(orgId: string | undefined) {
  const qc = useQueryClient();
  const invalidate = useOrgTeamInvalidate(orgId);
  return useApiMutation<void, { memberId: string; workspacePublicIds?: string[] }>(
    ({ memberId }) => ({
      path: `/organizations/${orgId}/team/${memberId}`,
      init: { method: 'DELETE' },
      fallback: 'Failed to remove member',
    }),
    {
      onSuccess: (_data, { workspacePublicIds }) =>
        Promise.all([
          invalidate() ?? Promise.resolve(),
          ...(workspacePublicIds ?? []).map((wsId) =>
            qc.invalidateQueries({ queryKey: qk.workspace(wsId, 'members') }),
          ),
        ]),
    },
  );
}

export function useUpdateTeamMemberProfile(orgId: string | undefined) {
  const invalidate = useOrgTeamInvalidate(orgId);
  return useApiMutation<void, UpdateTeamMemberProfileVars>(
    ({ memberId, firstName, lastName, phone }) => ({
      path: `/organizations/${orgId}/team/${memberId}/profile`,
      init: {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, phone }),
      },
      fallback: 'Failed to update profile',
    }),
    { onSuccess: invalidate },
  );
}

export function useUpdateTeamMemberRole(orgId: string | undefined) {
  const invalidate = useOrgTeamInvalidate(orgId);
  return useApiMutation<void, UpdateTeamMemberRoleVars>(
    ({ memberId, role }) => ({
      path: `/organizations/${orgId}/team/${memberId}`,
      init: {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      },
      fallback: 'Failed to update role',
    }),
    { onSuccess: invalidate },
  );
}

export function useAssignTeamMemberWorkspace(orgId: string | undefined) {
  const invalidate = useOrgTeamAndWorkspaceMembersInvalidate(orgId);
  return useApiMutation<void, AssignTeamMemberWorkspaceVars>(
    ({ workspacePublicId, organizationMemberPublicId, role }) => ({
      path: `/workspaces/${workspacePublicId}/members/assign`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationMemberPublicId, role }),
      },
      fallback: 'Failed to add workspace',
    }),
    { onSuccess: (_data, vars) => invalidate(vars.workspacePublicId) },
  );
}

export function useUnassignTeamMemberWorkspace(orgId: string | undefined) {
  const invalidate = useOrgTeamAndWorkspaceMembersInvalidate(orgId);
  return useApiMutation<void, UnassignTeamMemberWorkspaceVars>(
    ({ workspacePublicId, memberPublicId }) => ({
      path: `/workspaces/${workspacePublicId}/members/${memberPublicId}`,
      init: { method: 'DELETE' },
      fallback: 'Failed to remove workspace',
    }),
    { onSuccess: (_data, vars) => invalidate(vars.workspacePublicId) },
  );
}
